import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import ProjectDetailsStep from './components/ProjectDetailsStep';
import PropertyInfoStep from './components/PropertyInfoStep';
import TimelineBudgetStep from './components/TimelineBudgetStep';
import ContactPreferencesStep from './components/ContactPreferencesStep';
import NavigationButtons from './components/NavigationButtons';
import ProgressIndicator from './components/ProgressIndicator';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';

const QuoteRequestForm = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1 - Project Details
    projectType: '',
    serviceType: '',
    projectDescription: '',
    propertyPhotos: [],
    
    // Step 2 - Property Information
    homeType: '',
    propertySize: '',
    buildYear: '',
    currentEnergySystems: [],
    postalCode: '',
    city: '',
    address: '',
    
    // Step 3 - Timeline & Budget
    urgency: 'medium',
    preferredStartDate: '',
    budgetRange: '',
    financingNeeded: false,
    
    // Step 4 - Contact Preferences
    customerName: '',
    customerEmail: '',
    phone: '',
    preferredContactMethod: 'email',
    availabilityWindows: [],
    additionalNotes: ''
  });
  const [errors, setErrors] = useState({});
  const [matchedLeveranciers, setMatchedLeveranciers] = useState([]);

  const totalSteps = 4;

  // Load saved draft from localStorage
  useEffect(() => {
    const savedDraft = localStorage.getItem('quoteRequestDraft');
    if (savedDraft) {
      try {
        const parsedDraft = JSON.parse(savedDraft);
        setFormData(parsedDraft);
      } catch (error) {
        console.error('Error loading saved draft:', error);
      }
    }
  }, []);

  // Pre-fill user data if authenticated
  useEffect(() => {
    if (user && userProfile) {
      setFormData(prev => ({
        ...prev,
        customerName: userProfile?.full_name || '',
        customerEmail: user?.email || ''
      }));
    }
  }, [user, userProfile]);

  // Load matched leveranciers based on postal code and service type
  useEffect(() => {
    if (formData?.postalCode && formData?.serviceType) {
      loadMatchedLeveranciers();
    }
  }, [formData?.postalCode, formData?.serviceType]);

  const loadMatchedLeveranciers = async () => {
    try {
      const postalPrefix = formData?.postalCode?.substring(0, 4);
      const { data, error } = await supabase?.from('provider_service_areas')?.select('provider_id, user_profiles!inner(full_name, email)')?.or(`postal_code_range.ilike.%${postalPrefix}%,province.ilike.%${formData?.city}%`);

      if (!error && data) {
        setMatchedLeveranciers(data?.slice(0, 3) || []);
      }
    } catch (err) {
      console.error('Error loading leveranciers:', err);
    }
  };

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    // Clear related errors
    const updatedFields = Object.keys(updates);
    setErrors(prev => {
      const newErrors = { ...prev };
      updatedFields?.forEach(field => {
        delete newErrors?.[field];
      });
      return newErrors;
    });
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.projectType) newErrors.projectType = 'Selecteer een projecttype';
      if (!formData?.serviceType) newErrors.serviceType = 'Selecteer een dienst';
      if (!formData?.projectDescription?.trim()) newErrors.projectDescription = 'Beschrijving is verplicht';
    }

    if (step === 2) {
      if (!formData?.homeType) newErrors.homeType = 'Selecteer een woningtype';
      if (!formData?.postalCode?.trim()) newErrors.postalCode = 'Postcode is verplicht';
      else if (!/^[1-9][0-9]{3}\s?[A-Z]{2}$/i?.test(formData?.postalCode)) {
        newErrors.postalCode = 'Ongeldige Nederlandse postcode';
      }
      if (!formData?.city?.trim()) newErrors.city = 'Plaats is verplicht';
    }

    if (step === 3) {
      if (!formData?.budgetRange) newErrors.budgetRange = 'Selecteer een budgetbereik';
      if (!formData?.urgency) newErrors.urgency = 'Selecteer urgentie';
    }

    if (step === 4) {
      if (!formData?.customerName?.trim()) newErrors.customerName = 'Naam is verplicht';
      if (!formData?.customerEmail?.trim()) newErrors.customerEmail = 'E-mailadres is verplicht';
      else if (!/\S+@\S+\.\S+/?.test(formData?.customerEmail)) {
        newErrors.customerEmail = 'Ongeldig e-mailadres';
      }
      if (!formData?.phone?.trim()) newErrors.phone = 'Telefoonnummer is verplicht';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveDraft = () => {
    localStorage.setItem('quoteRequestDraft', JSON.stringify(formData));
    alert('Concept opgeslagen! U kunt later verdergaan.');
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase?.from('quote_requests')?.insert([{
          customer_name: formData?.customerName,
          customer_email: formData?.customerEmail,
          service_type: formData?.serviceType,
          location: `${formData?.city}, ${formData?.address || ''}`,
          postal_code: formData?.postalCode,
          urgency: formData?.urgency,
          description: `${formData?.projectDescription}\n\nWoningtype: ${formData?.homeType}\nBouwjaar: ${formData?.buildYear || 'Onbekend'}\nOppervlakte: ${formData?.propertySize || 'Onbekend'}\nHuidige systemen: ${formData?.currentEnergySystems?.join(', ') || 'Geen'}\n\nContactvoorkeur: ${formData?.preferredContactMethod}\nTelefoon: ${formData?.phone}\nBeschikbaarheid: ${formData?.availabilityWindows?.join(', ') || 'Flexibel'}\n\nExtra notities: ${formData?.additionalNotes || 'Geen'}`,
          budget_range: formData?.budgetRange,
          status: 'pending'
        }])?.select();

      if (error) throw error;

      // Clear draft
      localStorage.removeItem('quoteRequestDraft');
      
      // Navigate to success page or dashboard
      alert('Uw offerte-aanvraag is succesvol verzonden! Leveranciers zullen binnenkort contact met u opnemen.');
      navigate('/homepage');
    } catch (err) {
      console.error('Error submitting quote:', err);
      alert(`Fout bij verzenden: ${err?.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <ProjectDetailsStep formData={formData} updateFormData={updateFormData} errors={errors} />;
      case 2:
        return <PropertyInfoStep formData={formData} updateFormData={updateFormData} errors={errors} />;
      case 3:
        return <TimelineBudgetStep formData={formData} updateFormData={updateFormData} errors={errors} />;
      case 4:
        return <ContactPreferencesStep formData={formData} updateFormData={updateFormData} errors={errors} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <MainNavigation />

      {/* Main Content with sidebar spacing */}
      <main className="pt-16 lg:pt-0 lg:ml-[72px] transition-all duration-300">
      {/* Header */}
      <div className="bg-card border-b border-border shadow-elevation-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Icon name="FileText" size={20} className="text-primary sm:w-6 sm:h-6" />
              <h1 className="text-xl font-bold text-foreground">Offerte Aanvragen</h1>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <ProgressIndicator currentStep={currentStep} totalSteps={totalSteps} />

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24 sm:pb-32">
        <div className="bg-card rounded-lg shadow-sm border border-border p-4 sm:p-6">
          {renderStep()}
        </div>
      </div>

      {/* Navigation Buttons - Fixed at bottom on mobile */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-elevation-2 lg:relative lg:shadow-none">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <NavigationButtons
            currentStep={currentStep}
            totalSteps={totalSteps}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onSaveDraft={handleSaveDraft}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            canProceed={true}
          />
        </div>
      </div>
      </main>
    </div>
  );
};

export default QuoteRequestForm;